import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

// Icons (imported from Heroicons)
import { 
  HomeIcon, 
  ViewGridIcon, 
  UserGroupIcon,
  LogoutIcon,
  MenuIcon, 
  XIcon,
  CogIcon
} from '@heroicons/react/outline';

const AdminLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: (props: any) => <HomeIcon {...props} /> },
    { name: 'Solutions', href: '/admin/solutions', icon: (props: any) => <ViewGridIcon {...props} /> },
    { name: 'Contacts', href: '/admin/contacts', icon: (props: any) => <UserGroupIcon {...props} /> },
    { name: 'Users', href: '/admin/users', icon: (props: any) => <UserGroupIcon {...props} /> },
    { name: 'Settings', href: '/admin/settings', icon: (props: any) => <CogIcon {...props} /> },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Check if a nav link is active
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <div className="flex flex-1 pt-16 overflow-hidden bg-background dark:bg-dark-background">
        {/* Mobile sidebar */}
        <div className={`${sidebarOpen ? 'fixed inset-0 flex z-40 md:hidden' : 'hidden'}`}>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-foreground/30 dark:bg-dark-foreground/30" 
            onClick={() => setSidebarOpen(false)}
          />
          
          {/* Sidebar */}
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-card dark:bg-dark-card">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary dark:focus:ring-dark-primary"
                onClick={() => setSidebarOpen(false)}
              >
                <span className="sr-only">Close sidebar</span>
                <XIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4">
                <h1 className="text-xl font-bold text-primary dark:text-slate-100">
                  Admin Panel
                </h1>
              </div>
              
              <nav className="mt-5 px-2 space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${
                      isActive(item.href)
                        ? 'bg-primary/10 text-primary dark:bg-primary-300/20 dark:text-slate-100'
                        : 'text-foreground dark:text-slate-300 hover:bg-muted hover:text-foreground dark:hover:bg-dark-muted dark:hover:text-white'
                    }`}
                  >
                    {item.icon({
                      className: `mr-4 h-6 w-6 ${
                        isActive(item.href)
                          ? 'text-primary dark:text-slate-100'
                          : 'text-foreground/60 dark:text-slate-300 group-hover:text-foreground dark:group-hover:text-white'
                      }`
                    })}
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
            
            <div className="flex-shrink-0 flex border-t border-border dark:border-dark-border p-4">
              <div className="flex-shrink-0 w-full group block">
                <div className="flex items-center">
                  <div>
                    <div className="h-9 w-9 rounded-full bg-primary/20 dark:bg-primary-300/30 flex items-center justify-center">
                      <span className="text-primary dark:text-slate-100 font-medium text-sm">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-foreground dark:text-slate-100">
                      {user?.name}
                    </p>
                    <button
                      onClick={handleLogout}
                      className="flex items-center text-xs text-primary dark:text-slate-300 hover:text-primary/90 dark:hover:text-white"
                    >
                      <LogoutIcon className="mr-1 h-4 w-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop sidebar */}
        <div className="hidden md:flex md:flex-shrink-0">
          <div className="flex flex-col w-64">
            <div className="flex-1 flex flex-col min-h-0 bg-card dark:bg-dark-card">
              <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
                <div className="flex items-center flex-shrink-0 px-4">
                  <h1 className="text-xl font-bold text-primary dark:text-slate-100">
                    Admin Panel
                  </h1>
                </div>
                
                <nav className="mt-5 flex-1 px-2 space-y-1">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive(item.href)
                          ? 'bg-primary/10 text-primary dark:bg-primary-300/20 dark:text-slate-100'
                          : 'text-foreground dark:text-slate-300 hover:bg-muted dark:hover:bg-dark-muted hover:text-foreground dark:hover:text-white'
                      }`}
                    >
                      {item.icon({
                        className: `mr-3 h-5 w-5 ${
                          isActive(item.href)
                            ? 'text-primary dark:text-slate-100'
                            : 'text-foreground/60 dark:text-slate-300 group-hover:text-foreground dark:group-hover:text-white'
                        }`
                      })}
                      {item.name}
                    </Link>
                  ))}
                </nav>
              </div>
              
              <div className="flex-shrink-0 flex border-t border-border dark:border-dark-border p-4">
                <div className="flex-shrink-0 w-full group block">
                  <div className="flex items-center">
                    <div>
                      <div className="h-9 w-9 rounded-full bg-primary/20 dark:bg-primary-300/30 flex items-center justify-center">
                        <span className="text-primary dark:text-slate-100 font-medium text-sm">
                          {user?.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-foreground dark:text-slate-100">
                        {user?.name}
                      </p>
                      <button
                        onClick={handleLogout}
                        className="flex items-center text-xs text-primary dark:text-slate-300 hover:text-primary/90 dark:hover:text-white"
                      >
                        <LogoutIcon className="mr-1 h-4 w-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col w-0 flex-1 overflow-hidden">
          <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3">
            <button
              type="button"
              className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-foreground dark:text-dark-foreground hover:text-primary dark:hover:text-dark-primary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary dark:focus:ring-dark-primary"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <MenuIcon className="h-6 w-6" />
            </button>
          </div>
          
          <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
            <div className="py-6">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                <Outlet />
              </div>
            </div>
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminLayout; 